import { useState } from "react";
import { createRoot } from "react-dom/client";
import { imageVariantUrl } from "./image-variant";
import "./styles.css";

type DownloadTarget = {
  label: string;
  platform: string;
  url: string;
};

const MINI_PROGRAM_URL = import.meta.env.VITE_MINI_PROGRAM_URL?.trim() ?? "";
const MINI_PROGRAM_QR_URL = import.meta.env.VITE_MINI_PROGRAM_QR_URL?.trim() ?? "";
const BRAND_APP_ICON = "/images/brand/shapemoto-icon-flat.png?v=2-flow-s";

function BrandLockup() {
  return (
    <span className="brand-lockup">
      <img src={BRAND_APP_ICON} alt="" aria-hidden="true" />
      <span><strong>SHAPE MOTO</strong><small>型动</small></span>
    </span>
  );
}

const appDownloadTargets: DownloadTarget[] = [
  {
    label: "App Store",
    platform: "iPhone / iPad",
    url: import.meta.env.VITE_IOS_APP_URL?.trim() ?? ""
  },
  {
    label: "Android",
    platform: "Android 设备",
    url: import.meta.env.VITE_ANDROID_APP_URL?.trim() ?? ""
  }
];

const featuredCases = [
  {
    className: "case-tile-main",
    image: "/images/brand/cases/bmw-m2.jpg",
    title: "BMW M2",
    tag: "宽体 · 性能升级"
  },
  {
    className: "case-tile-abarth",
    image: "/images/brand/cases/abarth.jpg",
    title: "ABARTH",
    tag: "整车定制"
  },
  {
    className: "case-tile-g63",
    image: "/images/brand/cases/g63-amg-black.jpg",
    title: "G63 AMG BLACK",
    tag: "MS-09 DOM"
  }
];

const experienceHighlights = [
  { number: "01", title: "先看做过的车", description: "案例里有车型、改动项目和完工效果，先看看合不合适。" },
  { number: "02", title: "再聊你的需求", description: "把车型、预算和怎么用车告诉顾问，他会接着和你聊。" },
  { number: "03", title: "手机上继续跟进", description: "小程序看案例，型动 App 提交需求、收消息。" }
];

/**
 * 官网聚焦品牌表达和移动端入口分发，案例浏览与服务沟通由小程序和 App 承接。
 */
function LandingPage() {
  const [entryMessage, setEntryMessage] = useState("");

  /**
   * 手机端优先通过微信 URL Link 唤起小程序；未配置时给出明确的扫码提示。
   */
  function openMiniProgram() {
    if (!MINI_PROGRAM_URL) {
      setEntryMessage("小程序即将上线，你可以先提交咨询。");
      return;
    }

    window.location.assign(MINI_PROGRAM_URL);
  }

  /** 官网预约优先进入小程序；尚未配置小程序时仍可通过站内表单提交，不制造死按钮。 */
  function openAppointment() {
    if (MINI_PROGRAM_URL) {
      window.location.assign(MINI_PROGRAM_URL);
      return;
    }
    window.location.assign("/contact.html?topic=appointment");
  }

  return (
    <main className="landing-page">
      <header className="site-header">
        <a className="brand-link" href="#top" aria-label="返回 SHAPE MOTO 首页">
          <BrandLockup />
        </a>

        <nav className="site-nav" aria-label="首页导航">
          <a href="#experience">怎么开始</a>
          <a href="#download">手机入口</a>
          <button type="button" onClick={openMiniProgram} disabled={!MINI_PROGRAM_URL}>
            {MINI_PROGRAM_URL ? "进入小程序" : "小程序即将上线"}
          </button>
        </nav>
      </header>

      <section className="hero-section" id="top" aria-labelledby="landing-title">
        <div className="hero-copy">
          <p className="eyebrow"><span aria-hidden="true" /> SHAPE THE DRIVE</p>
          <h1 id="landing-title"><span>先看我们做过的车，</span><span>再聊你想怎么改。</span></h1>
          <p className="hero-description">
            SHAPE MOTO 做整车定制、外观升级，也做经典车修复。
            有想法，把车型、预算和平时怎么用车告诉我们。
          </p>

          <div className="hero-actions">
            <button className="primary-action" type="button" onClick={openAppointment}>
              聊聊我的车
              <span aria-hidden="true">→</span>
            </button>
            <a className="secondary-action" href="#download">查看手机入口</a>
          </div>

          {entryMessage ? <p className="entry-message" role="status">{entryMessage}</p> : null}

          <dl className="hero-stats" aria-label="品牌信息">
            <div><dt>BASE</dt><dd>成都</dd></div>
            <div><dt>FOCUS</dt><dd>汽车设计</dd></div>
            <div><dt>EST.</dt><dd>2025</dd></div>
          </dl>
        </div>

        <div className="hero-showcase" aria-label="SHAPE MOTO 改装案例">
          <div className="case-wall">
            {featuredCases.map((featuredCase) => (
              <figure className={`case-tile ${featuredCase.className}`} key={featuredCase.image}>
                <img
                  src={imageVariantUrl(featuredCase.image, 1280)}
                  srcSet={`${imageVariantUrl(featuredCase.image, 384)} 384w, ${imageVariantUrl(featuredCase.image, 768)} 768w, ${imageVariantUrl(featuredCase.image, 1280)} 1280w`}
                  sizes={featuredCase.className === "case-tile-main"
                    ? "(max-width: 840px) calc(100vw - 100px), (max-width: 1120px) 50vw, 680px"
                    : "(max-width: 840px) calc((100vw - 112px) / 2), (max-width: 1120px) 25vw, 334px"}
                  alt={`${featuredCase.title} ${featuredCase.tag}案例`}
                  loading={featuredCase.className === "case-tile-main" ? "eager" : "lazy"}
                  fetchPriority={featuredCase.className === "case-tile-main" ? "high" : "auto"}
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="experience-section" id="experience" aria-labelledby="experience-title">
        <div className="section-heading">
          <p className="eyebrow"><span aria-hidden="true" /> HOW IT WORKS</p>
          <h2 id="experience-title">先看案例，再聊需求，后面用手机跟进。</h2>
        </div>

        <div className="experience-grid">
          {experienceHighlights.map((highlight) => (
            <article key={highlight.number}>
              <span>{highlight.number}</span>
              <h3>{highlight.title}</h3>
              <p>{highlight.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="download-section" id="download" aria-labelledby="download-title">
        <div className="download-copy">
          <p className="eyebrow eyebrow-light"><span aria-hidden="true" /> MOBILE ACCESS</p>
          <h2 id="download-title">用手机继续跟进。</h2>
          <p>微信里看案例和动态；型动 App 提交需求、收消息、看进度。</p>
          <img className="download-mark" src={BRAND_APP_ICON} alt="" />
        </div>

        <section className="mini-program-card" aria-labelledby="mini-program-title">
          <div className={`mini-program-code ${MINI_PROGRAM_QR_URL ? "has-code" : "is-pending"}`}>
            {MINI_PROGRAM_QR_URL ? (
              <img src={MINI_PROGRAM_QR_URL} alt="SHAPE MOTO 微信小程序码" />
            ) : (
              <div className="code-pending" role="status">
                <img src={BRAND_APP_ICON} alt="" />
                <strong>小程序即将上线</strong>
                <span>上线前可以先用联系表单咨询</span>
              </div>
            )}
          </div>
          <div className="mini-program-content">
            <small>WECHAT MINI PROGRAM</small>
            <h3 id="mini-program-title">微信里先看看</h3>
            <p>看案例和动态，也可以提交需求</p>
            <button type="button" onClick={openMiniProgram} disabled={!MINI_PROGRAM_URL}>
              {MINI_PROGRAM_URL ? "打开小程序" : "即将上线"}
              <span aria-hidden="true">{MINI_PROGRAM_URL ? "↗" : "·"}</span>
            </button>
          </div>
        </section>

        <section className="app-card" aria-labelledby="app-download-title">
          <div className="app-card-heading">
            <img src={BRAND_APP_ICON} alt="" />
            <div>
              <small>型动 APP</small>
              <h3 id="app-download-title">安装型动 App</h3>
            </div>
          </div>

          <div className="app-downloads">
            {appDownloadTargets.map((target) => target.url ? (
              <a key={target.label} href={target.url} rel="noreferrer" target="_blank">
                <span><small>{target.platform}</small><strong>{target.label}</strong></span>
                <b aria-hidden="true">↓</b>
              </a>
            ) : (
              <button key={target.label} type="button" disabled title={`${target.label} 下载地址待配置`}>
                <span><small>{target.platform}</small><strong>{target.label}</strong></span>
                <b>即将上线</b>
              </button>
            ))}
          </div>
        </section>
      </section>

      <footer className="landing-footer">
        <BrandLockup />
        <p>© 2026 SHAPE MOTO. SHAPE THE DRIVE.</p>
        <nav className="filing-links" aria-label="网站备案信息">
          <a href="/privacy.html">隐私政策</a>
          <a href="/terms.html">用户协议</a>
          <a href="/account-deletion.html">账号删除</a>
          <a href="https://beian.miit.gov.cn/" rel="noreferrer" target="_blank">蜀ICP备2025129796号-1</a>
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=51011502000962" rel="noreferrer" target="_blank">
            川公网安备51011502000962号
          </a>
        </nav>
      </footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<LandingPage />);
